import { useState, useEffect, useRef } from "react";
import { CognitoUser, AuthenticationDetails } from "amazon-cognito-identity-js";
import { useDispatch } from "react-redux";
import { signInWithRedirect, signOut } from "aws-amplify/auth";
import { fetchAuthSession, getCurrentUser } from "aws-amplify/auth";
import Loader from '../../components/Loader';
import GifLogo from "../../images/main-logo.gif";
import DurianLogo from "../../images/durian-logo.png";
import GoogleIcon from "../../images/google-icon.png";
import Pencil from "../../images/pencil.png";
import { Link, useNavigate } from "react-router-dom";
import { getConfig, getUserInfo } from "../../api/auth";
import UserPool from "../../UserPool";
import { setAuth, setUser, setUserInfo } from "../../features/user/userSlice";
import { setReference } from "../../features/reference/referenceSlice";

const Login = () => {
  const initialLoginData = {
    email: "",
    password: "",
  };

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isLoading, setLoading] = useState(false);
  const [loginData, setLoginData] = useState(initialLoginData);
  const [showPassword, setShowPassword] = useState("password");
  const [error, setError] = useState("");
  const hasCheckedOAuth = useRef(false);

  // Password change state
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordChangeData, setPasswordChangeData] = useState({ newPassword: "", confirmPassword: "" });
  const [cognitoUserForPasswordChange, setCognitoUserForPasswordChange] = useState(null);
  const [userAttributesForPasswordChange, setUserAttributesForPasswordChange] = useState(null);

  // Check if user is returning from Google OAuth
  useEffect(() => {
    // Only run once - use ref to prevent multiple calls
    if (hasCheckedOAuth.current) return;

    hasCheckedOAuth.current = true;

    const checkGoogleAuth = async () => {
      try {
        setLoading(true);
        // Only check for OAuth if user has valid session tokens
        const session = await fetchAuthSession();

        // Check if session has valid tokens
        if (session.tokens && session.tokens.idToken) {
          const user = await getCurrentUser();
          const userData = session.tokens.idToken.payload;

          if (userData && user) {
            dispatch(setUser(userData));
            localStorage.setItem('user', JSON.stringify(userData));

            // Validate user against backend — no fallback for Google OAuth
            try {
              await preRequisiteCall();
              navigate('/');
            } catch (err) {
              // Sign out from Cognito/Amplify so session is cleared
              try { await signOut(); } catch (e) {}
              if (err.message === 'INACTIVE_USER') {
                setError('Your account is inactive. Please contact your Admin.');
              } else if (err.message === 'SERVICE_UNAVAILABLE') {
                setError('Service temporarily unavailable. Please try again later.');
              } else {
                setError('Invalid user. Please contact your Admin.');
              }
            }
          }
        }
      } catch (err) {
        // User not authenticated via Google or session expired, continue with normal flow
      } finally {
        setLoading(false);
      }
    };

    checkGoogleAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setLoginData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    setError("");
  };

  const preRequisiteCall = async (tokenData = null) => {
    try {
      const response = await getUserInfo();

      const userInfoData = response.data?.response?.userInfo;

      // Block inactive users before granting access
      const userStatus = userInfoData?.status || '';
      if (userStatus && userStatus.toLowerCase() !== 'active') {
        throw new Error('INACTIVE_USER');
      }

      localStorage.setItem('userInfo', JSON.stringify(userInfoData));
      dispatch(setUserInfo(userInfoData));
      dispatch(setAuth());

      // Fetch config data (non-blocking, non-critical)
      try {
        const res = await getConfig();
        const configData = res.data.data.value || res.data.data;
        localStorage.setItem('reference', JSON.stringify(configData));
        dispatch(setReference(configData));
      } catch (configErr) {
        // Non-critical, continue without config data
      }
    } catch (err) {
      // Re-throw user validation errors — these must block login
      if (err.message === 'INACTIVE_USER') throw err;

      // 404 = user not registered in the backend system
      if (err.response?.status === 404) {
        throw new Error('USER_NOT_FOUND');
      }

      // 503 = database temporarily unavailable — distinct from user errors
      if (err.response?.status === 503) {
        throw new Error('SERVICE_UNAVAILABLE');
      }

      // For infrastructure errors (CORS, 502, network) fall back to Cognito token data
      // so backend outages don't lock out valid users
      if (tokenData) {
        const nameParts = (tokenData.name || '').split(' ');
        const fallbackUserInfo = {
          user_name: tokenData.name || tokenData.email || 'User',
          first_name: tokenData.given_name || nameParts[0] || '',
          last_name: tokenData.family_name || nameParts.slice(1).join(' ') || '',
          company: tokenData['custom:company_id'] || null,
          client: tokenData['custom:client_id'] || null,
          user_id: tokenData.sub,
          email: tokenData.email,
        };
        localStorage.setItem('userInfo', JSON.stringify(fallbackUserInfo));
        dispatch(setUserInfo(fallbackUserInfo));
      }
      dispatch(setAuth());
    }
  }

  const handleGoogleSignIn = async () => {
    try {
      setLoading(true);
      await signInWithRedirect({ provider: 'Google' });
    } catch (err) {
      setLoading(false);
      setError(err.message || "Failed to sign in with Google");
    }
  };

  const handleLogin = async () => {
    setError("");

    // Validate inputs
    if (!loginData.email || !loginData.password) {
      setError("Please enter both username/email and password");
      return;
    }

    // Trim whitespace from username/email
    const username = loginData.email.trim();

    // Basic validation - just check it's not empty after trimming
    if (!username || !loginData.password.trim()) {
      setError("Please enter both username/email and password");
      return;
    }

    setLoading(true);
    const user = new CognitoUser({
      Username: username,
      Pool: UserPool
    });

    const authDetails = new AuthenticationDetails({
      Username: username,
      Password: loginData.password
    });

    user.authenticateUser(authDetails, {
      onSuccess: async (data) => {
        const userData = data.getIdToken().payload;
        dispatch(setUser(userData));
        localStorage.setItem('user', JSON.stringify(userData));

        // Validate user against backend
        try {
          await preRequisiteCall(userData);
          setLoading(false);
          navigate('/');
        } catch (err) {
          user.signOut();
          setLoading(false);
          if (err.message === 'INACTIVE_USER') {
            setError('Your account is inactive. Please contact your Admin.');
          } else if (err.message === 'SERVICE_UNAVAILABLE') {
            setError('Service temporarily unavailable. Please try again later.');
          } else {
            setError('Invalid user. Please contact your Admin.');
          }
        }
      },
      onFailure: err => {
        setLoading(false);

        if (err.code === "UserNotConfirmedException") {
          setError("Please verify your email before logging in");
        } else if (err.code === "NotAuthorizedException") {
          setError("Incorrect username/email or password");
        } else if (err.code === "UserNotFoundException") {
          setError("No account found with this username/email");
        } else if (err.code === "InvalidParameterException") {
          setError("Invalid username/email or password format");
        } else {
          setError(err.message || "Failed to login");
        }
      },
      newPasswordRequired: (userAttributes, _requiredAttributes) => {
        setLoading(false);

        // Remove attributes that shouldn't be updated or are read-only
        delete userAttributes.email_verified;
        delete userAttributes.phone_number_verified;
        delete userAttributes.email; // Email cannot be modified during password change
        delete userAttributes.sub; // User ID is immutable

        // Store the user object and attributes in state to complete password change
        setCognitoUserForPasswordChange(user);
        setUserAttributesForPasswordChange(userAttributes);
        setShowPasswordChange(true);
        setError("");
      }
    })
  };

  const handlePasswordChange = async () => {
    setError("");

    // Validate passwords match
    if (passwordChangeData.newPassword !== passwordChangeData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    // Validate password requirements
    if (passwordChangeData.newPassword.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    if (!cognitoUserForPasswordChange || !userAttributesForPasswordChange) {
      setError("Session expired. Please login again.");
      setShowPasswordChange(false);
      return;
    }

    setLoading(true);

    cognitoUserForPasswordChange.completeNewPasswordChallenge(
      passwordChangeData.newPassword,
      userAttributesForPasswordChange,
      {
        onSuccess: async (session) => {
          const userData = session.getIdToken().payload;
          dispatch(setUser(userData));
          localStorage.setItem('user', JSON.stringify(userData));

          // Validate user against backend
          try {
            await preRequisiteCall(userData);
            setLoading(false);
            navigate('/');
          } catch (err) {
            cognitoUserForPasswordChange.signOut();
            setLoading(false);
            setShowPasswordChange(false);
            if (err.message === 'INACTIVE_USER') {
              setError('Your account is inactive. Please contact your Admin.');
            } else if (err.message === 'SERVICE_UNAVAILABLE') {
              setError('Service temporarily unavailable. Please try again later.');
            } else {
              setError('Invalid user. Please contact your Admin.');
            }
          }
        },
        onFailure: (err) => {
          setLoading(false);
          setError(err.message || "Failed to change password");
        }
      }
    );
  };

  return (
    <div className="flex flex-col md:flex-row justify-center md:justify-between w-full h-auto min-h-screen bg-gradient-to-b from-primary-500 to-secondary-500 py-6 px-4 md:py-10 md:px-16">
      {isLoading && <Loader />}
      <div className="hidden md:flex flex-col gap-5 h-full">
        <div className="flex items-center gap-3">
          <img src={DurianLogo} alt="Durian Technologies" className="h-[50px]" style={{ filter: 'brightness(0) invert(1)' }} />
        </div>
        {/* <div className="flex flex-col justify-between "> */}
        <div className="flex justify-center w-[485px]">
          <img src={GifLogo} alt="main-logo" className="w-[350px]" />
        </div>
        <div className="w-[485px]">
          <h1 className="text-center text-text-light">
            <span className="text-[45px] font-bold">CMS crm</span>
            <br />
            <span className="text-[36px] whitespace-nowrap">Durian Technologies Inc.</span>
          </h1>
        </div>
        {/* </div> */}
      </div>
      <div className="flex flex-col justify-between items-center p-8 md:p-16 bg-white rounded-lg w-full md:w-[430px]">
        {!showPasswordChange ? (
          <form onSubmit={(e) => { e.preventDefault(); handleLogin(); }} className="flex flex-col justify-between items-center h-full w-full">
            <h1 className="text-4xl">Login</h1>
            <div>
              <div className="flex flex-col gap-8">
                <button
              onClick={handleGoogleSignIn}
              type="button"
              disabled={isLoading}
              className="flex gap-3 py-2 px-3 shadow-lg rounded-full hover:shadow-xl transition-shadow cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <img src={GoogleIcon} alt="google icon" className="w-6" />
              <p>Continue with your Account</p>
            </button>
                <div className="">
                  <label htmlFor="email-id" className="text-text-hint">
                    User Name / Email<span className="text-red-500">*</span>
                  </label>
                  <div className="flex  items-center px-2 border border-neutral-500 rounded-lg">
                    <input
                      onChange={handleChange}
                      value={loginData.email}
                      name="email"
                      type="text"
                      required
                      placeholder="username or email"
                      className="outline-none border-none focus:ring-0 flex-grow"
                    />
                    <img src={Pencil} alt="pencil" className="w-6 h-6" />
                  </div>
                </div>
                <div className="">
                  <label htmlFor="password" className="text-text-hint">
                    Password<span className="text-red-500">*</span>
                  </label>
                  <div className="flex  items-center  px-2 border border-neutral-500 rounded-lg">
                    <input
                      onChange={handleChange}
                      value={loginData.password}
                      name="password"
                      type={showPassword}
                      required
                      placeholder="password"
                      className="outline-none border-none focus:ring-0 flex-grow"
                    />
                {showPassword === "password" ? (
                  <svg
                    onClick={() => setShowPassword("text")}
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g id="icon">
                      <path
                        id="Ellipse 65_light"
                        d="M15.5 12C15.5 13.933 13.933 15.5 12 15.5C10.067 15.5 8.5 13.933 8.5 12C8.5 10.067 10.067 8.5 12 8.5C13.933 8.5 15.5 10.067 15.5 12Z"
                        stroke="#2F2F2F"
                      />
                      <path
                        id="Ellipse 66_light"
                        d="M20.188 10.9343C20.5762 11.4056 20.7703 11.6412 20.7703 12C20.7703 12.3588 20.5762 12.5944 20.188 13.0657C18.7679 14.7899 15.6357 18 12 18C8.36427 18 5.23206 14.7899 3.81197 13.0657C3.42381 12.5944 3.22973 12.3588 3.22973 12C3.22973 11.6412 3.42381 11.4056 3.81197 10.9343C5.23206 9.21014 8.36427 6 12 6C15.6357 6 18.7679 9.21014 20.188 10.9343Z"
                        stroke="#2F2F2F"
                      />
                    </g>
                  </svg>
                ) : (
                  <svg
                    onClick={() => setShowPassword("password")}
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g id="icon">
                      <path
                        id="Subtract_light"
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M15.9202 12.7988C15.9725 12.5407 16 12.2736 16 12C16 9.79086 14.2091 8 12 8C11.7264 8 11.4593 8.02746 11.2012 8.07977L12.1239 9.00251C13.6822 9.06583 14.9342 10.3178 14.9975 11.8761L15.9202 12.7988ZM9.39311 10.5143C9.14295 10.9523 9 11.4595 9 12C9 13.6569 10.3431 15 12 15C12.5405 15 13.0477 14.857 13.4857 14.6069L14.212 15.3332C13.5784 15.7545 12.8179 16 12 16C9.79086 16 8 14.2091 8 12C8 11.1821 8.24547 10.4216 8.66676 9.78799L9.39311 10.5143Z"
                        fill="#2F2F2F"
                      />
                      <path
                        id="Subtract_light_2"
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M16.1537 17.2751L15.4193 16.5406C14.3553 17.1196 13.1987 17.5 12 17.5C10.3282 17.5 8.73816 16.7599 7.36714 15.7735C6.00006 14.79 4.89306 13.5918 4.19792 12.7478C3.77356 12.2326 3.72974 12.1435 3.72974 12C3.72974 11.8565 3.77356 11.7674 4.19792 11.2522C4.86721 10.4396 5.9183 9.29863 7.21572 8.33704L6.50139 7.62271C5.16991 8.63072 4.10383 9.79349 3.42604 10.6164L3.36723 10.6876C3.03671 11.087 2.72974 11.4579 2.72974 12C2.72974 12.5421 3.0367 12.913 3.36723 13.3124L3.42604 13.3836C4.15099 14.2638 5.32014 15.5327 6.78312 16.5853C8.24216 17.635 10.0361 18.5 12 18.5C13.5101 18.5 14.9196 17.9886 16.1537 17.2751ZM9.18993 6.06861C10.0698 5.71828 11.0135 5.5 12 5.5C13.9639 5.5 15.7579 6.365 17.2169 7.41472C18.6799 8.46727 19.849 9.73623 20.574 10.6164L20.6328 10.6876C20.9633 11.087 21.2703 11.4579 21.2703 12C21.2703 12.5421 20.9633 12.913 20.6328 13.3124L20.574 13.3836C20.0935 13.9669 19.418 14.721 18.5911 15.4697L17.883 14.7617C18.6787 14.0456 19.3338 13.3164 19.8021 12.7478C20.2265 12.2326 20.2703 12.1435 20.2703 12C20.2703 11.8565 20.2265 11.7674 19.8021 11.2522C19.107 10.4082 18 9.21001 16.6329 8.22646C15.2619 7.24007 13.6718 6.5 12 6.5C11.3056 6.5 10.6253 6.62768 9.96897 6.84765L9.18993 6.06861Z"
                        fill="#2F2F2F"
                      />
                      <path
                        id="Vector 116_light"
                        d="M5 2L21 18"
                        stroke="#2F2F2F"
                      />
                    </g>
                  </svg>
                    )}

                    {/* <img src={Pencil} alt="pencil" className="w-6 h-6" /> */}
                  </div>
                </div>
              </div>

              {error && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}

              <div className="flex justify-between text-xs mt-4">
                <div className="flex gap-2">
                  <input type="checkbox" className="rounded-md" />
                  <p>Remember me</p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <Link to="/forgot-password" className="text-info-500">
                    Forgot Password?
                  </Link>
                  <Link to="/update-password" className="text-info-500">
                    Change Password
                  </Link>
                </div>
              </div>

            </div>
            <div className="flex flex-col justify-between gap-2 items-center w-full">
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-2 px-4 text-base rounded-full text-white bg-secondary-600 hover:bg-secondary-700 disabled:bg-neutral-300 disabled:cursor-not-allowed"
              >
                {isLoading ? "Logging in..." : "Login"}
              </button>

              <p className="text-sm text-center ">
                Don't you have an account?{" "}
                <Link to="/signup" className="text-info-500">
                  SignUp
                </Link>
              </p>
              <p className="text-text-hint">or</p>
              <p>
                <Link to="/subscribe" className="text-info-500 font-bold">
                  Subscribe
                </Link>{" "}
                for your Business
              </p>
            </div>
          </form>
        ) : (
          <form onSubmit={(e) => { e.preventDefault(); handlePasswordChange(); }} className="flex flex-col justify-between items-center h-full w-full">
            <h1 className="text-4xl">Change Password</h1>
            <div>
              <div className="flex flex-col gap-8">
                <p className="text-text-hint text-center text-sm">
                  You are required to change your temporary password before continuing.
                </p>

                <div className="">
                  <label htmlFor="newPassword" className="text-text-hint">
                    New Password<span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center px-2 border border-neutral-500 rounded-lg">
                    <input
                      id="newPassword"
                      type="password"
                      required
                      value={passwordChangeData.newPassword}
                      onChange={(e) => setPasswordChangeData({...passwordChangeData, newPassword: e.target.value})}
                      placeholder="Enter new password"
                      className="outline-none border-none focus:ring-0 flex-grow"
                    />
                  </div>
                </div>

                <div className="">
                  <label htmlFor="confirmPassword" className="text-text-hint">
                    Confirm New Password<span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center px-2 border border-neutral-500 rounded-lg">
                    <input
                      id="confirmPassword"
                      type="password"
                      required
                      value={passwordChangeData.confirmPassword}
                      onChange={(e) => setPasswordChangeData({...passwordChangeData, confirmPassword: e.target.value})}
                      placeholder="Confirm new password"
                      className="outline-none border-none focus:ring-0 flex-grow"
                    />
                  </div>
                </div>
              </div>

              <div className="text-xs text-text-hint mt-4">
                <p className="font-semibold mb-1">Password must contain:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>At least 8 characters</li>
                  <li>One uppercase letter (A-Z)</li>
                  <li>One lowercase letter (a-z)</li>
                  <li>One number (0-9)</li>
                  <li>One special character</li>
                </ul>
              </div>

              {error && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}
            </div>
            <div className="flex flex-col justify-between gap-2 items-center w-full">
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-2 px-4 text-base rounded-full text-white bg-secondary-600 hover:bg-secondary-700 disabled:bg-neutral-300 disabled:cursor-not-allowed"
              >
                {isLoading ? "Changing Password..." : "Change Password"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default Login;
